import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { ja } from 'date-fns/locale'
import type { FC } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { FieldBlock } from './FieldBlock'
import {
  type ClientErrors,
  INTERVAL_UNIT_OF,
  LAST_DAY_OPTIONS,
  MAX_INTERVAL,
  MAX_MONTH_DAY,
  REPEAT_OPTIONS,
  type RepeatKind,
  type TaskScheduleForm,
  UNIT_LABEL,
  WEEKDAYS,
  dateDefaultsFor,
  formatDateInput,
  parseDateInput,
  visibleFieldsOf,
} from './taskScheduleForm'
import { ToggleChips } from './ToggleChips'

type Props = {
  /** 試算 API が返した、この欄の理由 */
  errorsOf: (field: string) => string[]
  /** フォームの判定で見つかった不正 */
  clientErrors: ClientErrors
}

const INTERVAL_SUFFIX: Record<RepeatKind, string> = {
  daily: '',
  ndays: '日おき',
  weekly: '週ごと',
  monthly: 'か月ごと',
  yearly: '年ごと',
}

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}月`,
}))
const MONTH_DAY_OPTIONS = Array.from({ length: MAX_MONTH_DAY }, (_, i) => ({
  value: i + 1,
  label: `${i + 1}`,
}))

const toInt = (value: string, fallback: number) => {
  const n = parseInt(value, 10)
  return Number.isNaN(n) ? fallback : n
}

const asList = (message: string | undefined) => (message ? [message] : [])

/**
 * 適用期間・繰り返し・日付指定・予定期間・時刻。
 * 欄の出入りは visibleFieldsOf に従う。欄の値だけで決まる判定は clientErrors、規則の正はサーバの理由（errorsOf）で出す。
 */
export const RecurrenceSection: FC<Props> = ({ errorsOf, clientErrors }) => {
  const { control, setValue, getValues } = useFormContext<TaskScheduleForm>()
  const form = useWatch({ control }) as TaskScheduleForm
  const visible = visibleFieldsOf(form)
  const unit = INTERVAL_UNIT_OF[form.repeat]
  const unitLabel = UNIT_LABEL[unit]

  // 繰り返しを変えたら、間隔と予定期間を新しい頻度の初期値に戻す（前の頻度の値を引き継ぐと上限を超えうる）
  const onRepeatChange = (repeat: RepeatKind) => {
    const nextUnit = INTERVAL_UNIT_OF[repeat]
    const current = getValues('interval')
    const interval =
      repeat === 'ndays'
        ? Math.max(2, Math.min(current, MAX_INTERVAL[nextUnit]))
        : 1
    setValue('repeat', repeat, { shouldDirty: true })
    setValue('interval', interval)
    setValue('plannedPeriodKind', 'singleDay')
    setValue('plannedPeriodValue', 1)
  }

  // 適用開始日を変えたら、日付指定の初期値をその日から採り直す
  const onFromDateChange = (fromDate: string) => {
    setValue('fromDate', fromDate, { shouldDirty: true })
    if (fromDate === '') return
    const defaults = dateDefaultsFor(fromDate)
    setValue('weekdays', defaults.weekdays)
    setValue('months', defaults.months)
    setValue('monthDays', defaults.monthDays)
  }

  return (
    <Stack spacing={2}>
      <FieldBlock
        label="適用期間"
        required
        errors={[
          ...asList(clientErrors.fromDate),
          ...asList(clientErrors.throughDate),
          ...errorsOf('fromDate'),
          ...errorsOf('throughDate'),
        ]}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
          <Stack spacing={1}>
            <Controller
              control={control}
              name="hasThroughDate"
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      id="task-schedule-has-through-date"
                      checked={field.value}
                      onChange={e => {
                        field.onChange(e.target.checked)
                        if (
                          e.target.checked &&
                          getValues('throughDate') === ''
                        ) {
                          setValue('throughDate', getValues('fromDate'))
                        }
                      }}
                    />
                  }
                  label="適用終了日を設定する"
                />
              )}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <DatePicker
                format="yyyy/MM/dd"
                value={parseDateInput(form.fromDate)}
                minDate={new Date()}
                onChange={value =>
                  onFromDateChange(value ? formatDateInput(value) : '')
                }
                slotProps={{
                  textField: {
                    id: 'task-schedule-from-date',
                    size: 'small',
                    slotProps: { htmlInput: { placeholder: '開始日' } },
                  },
                  toolbar: { hidden: true },
                }}
                sx={{ flex: 1 }}
              />
              <Typography>{form.hasThroughDate ? '〜' : 'から適用'}</Typography>
              {form.hasThroughDate && (
                <Controller
                  control={control}
                  name="throughDate"
                  render={({ field }) => (
                    <DatePicker
                      format="yyyy/MM/dd"
                      value={parseDateInput(field.value)}
                      minDate={parseDateInput(form.fromDate) ?? undefined}
                      onChange={value =>
                        field.onChange(value ? formatDateInput(value) : '')
                      }
                      slotProps={{
                        textField: {
                          id: 'task-schedule-through-date',
                          size: 'small',
                          slotProps: { htmlInput: { placeholder: '終了日' } },
                        },
                        toolbar: { hidden: true },
                      }}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              )}
            </Stack>
          </Stack>
        </LocalizationProvider>
      </FieldBlock>

      <FieldBlock
        label="繰り返し"
        required
        errors={[
          ...errorsOf('recurrenceRule'),
          ...errorsOf('recurrenceRule.byDay'),
        ]}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ width: 240 }}>
            <Select<RepeatKind>
              id="task-schedule-repeat"
              inputProps={{ 'aria-label': '繰り返し' }}
              value={form.repeat}
              onChange={e => onRepeatChange(e.target.value as RepeatKind)}
            >
              {REPEAT_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {visible.interval && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Controller
                control={control}
                name="interval"
                render={({ field }) => (
                  <TextField
                    id="task-schedule-interval"
                    type="number"
                    size="small"
                    value={field.value}
                    onChange={e => field.onChange(toInt(e.target.value, 0))}
                    slotProps={{
                      htmlInput: {
                        'aria-label': '間隔',
                        min: form.repeat === 'ndays' ? 2 : 1,
                        max: MAX_INTERVAL[unit],
                      },
                    }}
                    sx={{ width: 96 }}
                  />
                )}
              />
              <Typography variant="body2">
                {INTERVAL_SUFFIX[form.repeat]}
              </Typography>
            </Stack>
          )}
        </Stack>
      </FieldBlock>

      {visible.weekdays && (
        <FieldBlock
          label="曜日"
          required
          errors={asList(clientErrors.weekdays)}
        >
          <Controller
            control={control}
            name="weekdays"
            render={({ field }) => (
              <ToggleChips
                ariaLabel="曜日"
                options={WEEKDAYS.map(w => ({
                  value: w.code,
                  label: w.label,
                }))}
                selected={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FieldBlock>
      )}

      {visible.months && (
        <FieldBlock label="月" required errors={asList(clientErrors.months)}>
          <Controller
            control={control}
            name="months"
            render={({ field }) => (
              <ToggleChips
                ariaLabel="月"
                options={MONTH_OPTIONS}
                selected={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FieldBlock>
      )}

      {visible.monthDays && (
        <FieldBlock
          label="日"
          required
          note="29〜31 日は選べません。月末を指定するときは「末日」を使ってください"
          errors={asList(clientErrors.monthDays)}
        >
          <Controller
            control={control}
            name="monthDays"
            render={({ field }) => (
              <Stack spacing={1}>
                <ToggleChips
                  ariaLabel="日"
                  options={MONTH_DAY_OPTIONS}
                  selected={field.value}
                  onChange={next =>
                    field.onChange([...next, ...field.value.filter(v => v < 0)])
                  }
                />
                <ToggleChips
                  ariaLabel="末日から数える日"
                  options={LAST_DAY_OPTIONS}
                  selected={field.value}
                  onChange={next =>
                    field.onChange([...field.value.filter(v => v > 0), ...next])
                  }
                />
              </Stack>
            )}
          />
        </FieldBlock>
      )}

      {visible.plannedPeriod && (
        <FieldBlock
          label="予定期間の長さ"
          required
          errors={asList(clientErrors.plannedPeriodValue)}
        >
          <RadioGroup
            value={form.plannedPeriodKind}
            onChange={e =>
              setValue(
                'plannedPeriodKind',
                e.target.value as TaskScheduleForm['plannedPeriodKind'],
                { shouldDirty: true },
              )
            }
          >
            <FormControlLabel
              value="singleDay"
              control={<Radio id="task-schedule-period-single-day" />}
              label={
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 40 }}>
                    日にち
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    その日のうちに提出する
                  </Typography>
                </Stack>
              }
            />
            <FormControlLabel
              value="sameUnit"
              control={<Radio id="task-schedule-period-same-unit" />}
              label={
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" sx={{ minWidth: 40 }}>
                    期間
                  </Typography>
                  <Controller
                    control={control}
                    name="plannedPeriodValue"
                    render={({ field }) => (
                      <TextField
                        id="task-schedule-period-value"
                        type="number"
                        size="small"
                        value={field.value}
                        onChange={e => {
                          field.onChange(toInt(e.target.value, 0))
                          setValue('plannedPeriodKind', 'sameUnit')
                        }}
                        slotProps={{
                          htmlInput: {
                            'aria-label': '予定期間の長さ',
                            min: 1,
                            max: form.interval,
                          },
                        }}
                        sx={{ width: 80 }}
                      />
                    )}
                  />
                  <Typography variant="body2">
                    {unitLabel}のあいだに提出する
                  </Typography>
                </Stack>
              }
            />
          </RadioGroup>
        </FieldBlock>
      )}

      {visible.time && (
        <FieldBlock
          label="時刻"
          required
          note="締切時刻が開始時刻より前のときは、翌日の締切になります"
          errors={[
            ...errorsOf('recurrenceRule.startTime'),
            ...errorsOf('recurrenceRule.dueTime'),
          ]}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <Controller
              control={control}
              name="startTime"
              render={({ field }) => (
                <TextField
                  {...field}
                  id="task-schedule-start-time"
                  type="time"
                  size="small"
                  slotProps={{
                    htmlInput: { 'aria-label': '開始時刻', step: 60 },
                  }}
                  sx={{ width: 160 }}
                />
              )}
            />
            <Typography variant="body2">〜</Typography>
            <Controller
              control={control}
              name="dueTime"
              render={({ field }) => (
                <TextField
                  {...field}
                  id="task-schedule-due-time"
                  type="time"
                  size="small"
                  slotProps={{
                    htmlInput: { 'aria-label': '締切時刻', step: 60 },
                  }}
                  sx={{ width: 160 }}
                />
              )}
            />
          </Stack>
        </FieldBlock>
      )}
    </Stack>
  )
}
